import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/styles.css';
import { ReactComponent as CoreBenefitsBoxSVG } from '../../../../assets/images/home/core-benefits-box.svg';
import { ReactComponent as CoreBenefitsChartSVG } from '../../../../assets/images/home/core-benefits-chart.svg';
import { ReactComponent as CoreBenefitsClockSVG } from '../../../../assets/images/home/core-benefits-clock.svg';
import { ReactComponent as CoreBenefitsDollarSVG } from '../../../../assets/images/home/core-benefits-dollar.svg';
import { ReactComponent as CoreBenefitsEyeSVG } from '../../../../assets/images/home/core-benefits-eye.svg';
import { ReactComponent as CoreBenefitsLockSVG } from '../../../../assets/images/home/core-benefits-lock.svg';
import { ReactComponent as CoreBenefitsPaperSVG } from '../../../../assets/images/home/core-benefits-paper.svg';
import { ReactComponent as CoreBenefitsRepeatSVG } from '../../../../assets/images/home/core-benefits-repeat.svg';
import { ReactComponent as CoreBenefitsSidebarSVG } from '../../../../assets/images/home/core-benefits-sidebar.svg';
import { ReactComponent as CoreBenefitsTabletSVG } from '../../../../assets/images/home/core-benefits-tablet.svg';
import { ReactComponent as CoreBenefitsUserCheckSVG } from '../../../../assets/images/home/core-benefits-user-check.svg';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import PreFooterBlock from '../../components/PreFooterBlock';
import './styles.scss';
import cx from 'classnames';

const coreBenefitsSteps = [
  {
    iconComponent: <CoreBenefitsTabletSVG />,
    title: 'Mobile Friendly',
    subtitle: 'Access documents on phone or tablet',
  },
  {
    iconComponent: <CoreBenefitsPaperSVG />,
    title: 'No More Paperwork',
    subtitle: 'Fully eliminate printed forms and filing cabinets',
  },
  {
    iconComponent: <CoreBenefitsClockSVG />,
    title: 'Save Time',
    subtitle: 'Reduce admin workload with instant delivery and tracking',
  },
  {
    iconComponent: <CoreBenefitsChartSVG />,
    title: 'Built-In Reporting',
    subtitle: 'Generate completion stats and compliance reports on demand',
  },
  {
    iconComponent: <CoreBenefitsEyeSVG />,
    title: 'Complete Oversight',
    subtitle: 'Visibility into all activity from any location',
  },
  {
    iconComponent: <CoreBenefitsSidebarSVG />,
    title: 'Modular & Custom',
    subtitle: 'Adapt workflows to your exact operational needs',
  },
  {
    iconComponent: <CoreBenefitsRepeatSVG />,
    title: 'Smarter Processes',
    subtitle: 'Automate routine tasks and documentation',
  },
  {
    iconComponent: <CoreBenefitsDollarSVG />,
    title: 'Cost Efficient',
    subtitle: 'Reduce labor, paper, printing, and storage costs',
  },
  {
    iconComponent: <CoreBenefitsUserCheckSVG />,
    title: 'Easy Staff Sign-Off',
    subtitle: 'Staff sign documents from links in email or text',
  },
  {
    iconComponent: <CoreBenefitsBoxSVG />,
    title: 'Document Vault',
    subtitle: 'Central storage for all signed, archived, and live forms',
  },
  {
    iconComponent: <CoreBenefitsLockSVG />,
    title: 'Secure & Compliant',
    subtitle: 'Safeguards sensitive data',
  },
];

const EducationPage = () => {
  const coreBenefitsSwiperRef = useRef();
  const images = [
    { src: '/images/home/see-in-action-1.png' },
    { src: '/images/home/see-in-action-2.png' },
    { src: '/images/home/see-in-action-3.png' },
    { src: '/images/home/see-in-action-4.png' },
  ];
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [isOpenScheduleDemoModal, setIsOpenScheduleDemoModal] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <main className={cx('home-page education-page', { isMobile })}>
      <Header setIsOpenScheduleDemoModal={setIsOpenScheduleDemoModal} isOpenScheduleDemoModal={isOpenScheduleDemoModal} />
      <div className="home-page-content">
        <div className="modernize-staff-education">
          <div className="modernize-left">
            <h2 className="modernize-title">
              <span className="blue-part">Modernize</span> <br /> Staff Education
            </h2>
            <p className="modernize-subtitle">Digitally assign, track, and store education records with ease.</p>
            <button className="request-demo-btn" onClick={() => setIsOpenScheduleDemoModal(true)}>
              Request a Demo{' '}
              <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.75 9.89771H14.25" stroke="#448AFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path
                  d="M9 4.64771L14.25 9.89771L9 15.1477"
                  stroke="#448AFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="manual-process">
          <div className="manual-process-left-block">
            <h3 className="say-goodbye-title">Switch to more Efficient, Creative and User Friendly Education Delivery</h3>
          </div>
          <div className="manual-process-right-block">
            <ul className="manual-process-list">
              <li className="manual-process-list-item">Stop Chasing Signatures</li>
              <li className="manual-process-list-item">Eliminate paper piles and binders</li>
              <li className="manual-process-list-item">Achieve higher completion rates</li>
              <li className="manual-process-list-item">Make education more exciting for staff</li>
              <li className="manual-process-list-item">Easy access to all completed education</li>
            </ul>
          </div>
        </div>

        <div className="core-capabilities">
          <h3 className="core-capabilities-title">Our Core Capabilities</h3>
          {isMobile ? (
            <div className="core-capabilities-mobile-wrapper">
              <div className="core-capabilities-row core-capabilities-row-1">
                <div className="core-capabilities-col">
                  <div className="core-capabilities-item-screenshot-wrapper">
                    <img className="core-capabilities-item-screenshot-img" src="/images/home/education-page-woman-phone.jpg" />
                  </div>
                  <div className="core-capabilities-item-content">
                    <div className="corner-image-wrapper">
                      <img className="corner-image" src="/images/home/core-capabilities-zap-mobile.svg" />
                    </div>
                    <h4 className="core-capabilities-item-title">
                      Instant Delivery <br />
                      via Text or Emails
                    </h4>
                  </div>
                </div>
              </div>

              <div className="core-capabilities-row core-capabilities-row-2">
                <div className="core-capabilities-col">
                  <div className="core-capabilities-item-content">
                    <div className="corner-image-wrapper">
                      <img className="corner-image" src="/images/home/core-capabilities-play-mobile.svg" />
                    </div>
                    <h4 className="core-capabilities-item-title">
                      Create
                      <br /> custom education content & videos
                    </h4>
                  </div>
                </div>
                <div className="core-capabilities-col">
                  <div className="core-capabilities-item-content">
                    <div className="corner-image-wrapper">
                      <img className="corner-image" src="/images/home/core-capabilities-tablet-mobile.svg" />
                    </div>
                    <h4 className="core-capabilities-item-title">Create company education libraries</h4>
                  </div>
                </div>
              </div>

              <div className="core-capabilities-row core-capabilities-row-3">
                <div className="core-capabilities-col">
                  <div className="core-capabilities-item-content">
                    <div className="corner-image-wrapper">
                      <img className="corner-image" src="/images/home/core-capabilities-progress-mobile.svg" />
                    </div>
                    <h4 className="core-capabilities-item-title">Continues Progress Tracking</h4>
                  </div>
                </div>
                <div className="core-capabilities-col">
                  <div className="core-capabilities-item-content">
                    <div className="corner-image-wrapper">
                      <img className="corner-image" src="/images/home/core-capabilities-file-mobile.svg" />
                    </div>
                    <h4 className="core-capabilities-item-title">Survey Friendly Completion Reports</h4>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="core-capabilities-grid-container">
              <div className="core-capabilities-item core-capabilities-item-tall">
                <div>
                  <img className="core-capabilities-item-screenshot-img" src="/images/home/education-page-woman-phone.jpg" />
                </div>
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-zap.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">
                    Instant Delivery <br />
                    via Text or Emails
                  </h4>
                </div>
              </div>
              <div className="core-capabilities-item core-capabilities-delivery">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-custom-content.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">Create custom education content & videos</h4>
                </div>
              </div>
              <div className="core-capabilities-item core-capabilities-custom-content">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-tablet.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">Create company education libraries</h4>
                </div>
              </div>
              <div className="core-capabilities-item">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-progress.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">Continues Progress Tracking</h4>
                </div>
              </div>
              <div className="core-capabilities-item">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-capabilities-file.svg" />
                </div>
                <div className="core-capabilities-item-content">
                  <h4 className="core-capabilities-item-title">Survey Friendly Completion Reports</h4>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="education-see-in-action">
          <h3 className="see-in-action-title">See It In Action</h3>
          <div className="see-in-action-images-wrapper">
            {images.map((image, idx) => (
              <div
                className="see-in-action-image-wrapper"
                key={idx}
                onClick={() => {
                  setIndex(idx);
                  setOpen(true);
                }}
                style={{ cursor: 'pointer' }}
              >
                <img className="see-in-action-img" src={image.src} alt={`See in action ${idx + 1}`} />
                <img className="scale-icon" src="/images/home/scale-icon.svg" />
              </div>
            ))}
          </div>

          <Lightbox
            open={open}
            close={() => setOpen(false)}
            index={index}
            slides={images}
            plugins={[Thumbnails]}
            thumbnails={{
              border: 0,
              vignette: false,
              height: 48,
              width: 48,
              padding: 0,
              gap: 14,
              imageFit: 'cover',
            }}
            styles={{
              container: {
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
              },
              slide: {
                width: '80vw',
                maxWidth: '80vw',
                maxHeight: '75vh',
                height: '75vh',
                margin: 'auto',
                borderRadius: '10px',
              },
            }}
          />
        </div>

        <div className="education-core-benefits">
          <h3 className="education-core-benefits-title">Core Benefits</h3>
          <div className="education-core-benefits-slider">
            <Swiper
              modules={[Navigation, Pagination]}
              onSwiper={(swiper) => (coreBenefitsSwiperRef.current = swiper)}
              spaceBetween={20}
              loop
              slidesPerView={isMobile ? 1 : 1.5}
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2.5 },
                1024: { slidesPerView: 2.5 },
              }}
            >
              {coreBenefitsSteps.map((step, index) => (
                <SwiperSlide key={index}>
                  <div className="education-core-benefits-slide">
                    <div style={{ height: '30px' }}></div>
                    <div className="icon-wrapper">{step.iconComponent}</div>
                    <div className="education-core-benefits-slide-content">
                      <p className="education-core-benefits-title">{step.title}</p>
                      <p className="education-core-benefits-subtitle">{step.subtitle}</p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="custom-nav">
              <button onClick={() => coreBenefitsSwiperRef.current?.slidePrev()}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 12.7954H5" stroke="#3571D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path
                    d="M12 19.7954L5 12.7954L12 5.79541"
                    stroke="#3571D6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button onClick={() => coreBenefitsSwiperRef.current?.slideNext()}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12.7954H19" stroke="#3571D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path
                    d="M12 5.79541L19 12.7954L12 19.7954"
                    stroke="#3571D6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <PreFooterBlock
          title="Ready to eliminate paper and optimize compliance?"
          subtitle="Customized solutions for every operational challenge — from education to audits."
          setIsOpenScheduleDemoModal={setIsOpenScheduleDemoModal}
        />
      </div>

      <Footer />
    </main>
  );
};

export default EducationPage;
